import { Spinner } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useMemo, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import * as yup from 'yup'
import AnimateHeight from '../../../components/AnimateHeight'
import FormButton from '../../../components/FormButton'
import FormInput from '../../../components/FormInput'
import MessageBox from '../../../components/MessageBox'
import {
  type VeliActivationLandingDto,
  useActivateVeliClientMutation,
  useGetVeliActivationLandingQuery,
} from '../../../redux/services/coreApi'
import { attemptAutoLoginAfterSignup } from '../../../utils/autoLoginAfterSignup'
import passwordValidator from '../../Login/utils/passwordValidator'
import VeliBrandedShell from './VeliBrandedShell'

type FormData = {
  confirmEmail: string
  firstName?: string
  lastName?: string
  phone?: string
  vatNumber?: string
  password: string
  confirmPassword: string
}

// An individual's name, phone and email are fixed from the import row and shown
// read-only/masked (ADR-0004 decision 8) — EXCEPT where the Veli extract left a hole
// (a client who never finished onboarding). For those holes the page collects the
// field and sends it; the server uses it only to fill the gap, never to overwrite a
// value the row holds. A company always sets a password and its phone (its legal
// identity is fixed from the row, and its phone isn't imported — issue #88).
//
// All also confirm their email: they type back the address their Veli account uses
// (shown only masked) as a light identity check so a wrong party who reached the
// non-secret link can't activate. It's a confirmation, never a new email — the server
// keeps the row's address and only gates on the match.
const confirmEmailShape = {
  confirmEmail: yup
    .string()
    .email('Enter a valid email address.')
    .required('This field is required.'),
}

const passwordShape = {
  password: passwordValidator().required('This field is required.'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match.')
    .required('This field is required.'),
}

// A company always sets a password and its phone. Its VAT number is asked for only
// where the import row carries none — the Veli export has no company columns at all,
// so a corporate row is flagged out of band and usually arrives with the legal name
// known and the VAT missing. The legal name is never re-typed either way.
const buildCompanySchema = (requiresVatNumber: boolean) =>
  yup.object({
    ...confirmEmailShape,
    phone: yup.string().required('This field is required.'),
    ...(requiresVatNumber
      ? { vatNumber: yup.string().required('This field is required.') }
      : {}),
    ...passwordShape,
  })

// The individual schema is built from the landing flags: name and/or phone inputs are
// required only when the import row left that field empty (otherwise it's fixed from
// the row and not rendered at all).
const buildIndividualSchema = (requiresName: boolean, requiresPhone: boolean) =>
  yup.object({
    ...confirmEmailShape,
    ...(requiresName
      ? {
          firstName: yup.string().required('This field is required.'),
          lastName: yup.string().required('This field is required.'),
        }
      : {}),
    ...(requiresPhone
      ? { phone: yup.string().required('This field is required.') }
      : {}),
    ...passwordShape,
  })

// Public activation entry for a transferring Veli client (issue #76, ADR-0004).
// Keyed on the (non-secret) Veli id in the URL, it resolves the landing data and
// hands the client a co-branded, hand-held path onto Penning: a welcome page,
// then a credentials page. Onboarding logic is untouched — this is presentation.
const VeliActivationScreen: React.FC = () => {
  const history = useHistory()
  const { veliUserId } = useParams<{ veliUserId: string }>()
  const {
    data: landing,
    isLoading,
    isError,
  } = useGetVeliActivationLandingQuery(veliUserId)

  if (isLoading) {
    return (
      <VeliBrandedShell>
        <div className="flex justify-center py-10">
          <Spinner size="xl" />
        </div>
      </VeliBrandedShell>
    )
  }

  if (isError || !landing) {
    return (
      <VeliBrandedShell subtitle="We couldn't open this activation link.">
        <div className="card gap-4 text-center">
          <h1 className="font-semi text-xl">This link is not valid</h1>
          <p className="text-description">
            We couldn't find an activation for this link. Please use the most
            recent link from Veli or your wealth manager, or contact{' '}
            <a className="text-brand-blue hover:underline" href="mailto:support@penning.io">
              support@penning.io
            </a>
            .
          </p>
        </div>
      </VeliBrandedShell>
    )
  }

  if (landing.state === 'AlreadyActivated') {
    return (
      <VeliBrandedShell subtitle="Your Penning account is ready.">
        <div className="card gap-4 text-center">
          <h1 className="font-semi text-xl">You're already set up</h1>
          <p className="text-description">
            This link has already been used to create your Penning account. Log
            in to continue where you left off.
          </p>
          <FormButton title="Log in instead" onClick={() => history.push('/login')} />
        </div>
      </VeliBrandedShell>
    )
  }

  return <ReadyToActivate veliUserId={veliUserId} landing={landing} />
}

// The ReadyToActivate path is a tiny two-step wizard: a welcome page the client
// reads, then a credentials page they fill in. Split out so the form hooks only
// mount once the landing data is known.
const ReadyToActivate: React.FC<{
  veliUserId: string
  landing: VeliActivationLandingDto
}> = ({ veliUserId, landing }) => {
  const history = useHistory()
  const isCompany = landing.clientType === 'Company'
  // An individual supplies the name/phone only where the Veli extract left it empty; a
  // field the row already holds stays fixed server-side and is shown masked, not asked.
  const needsName = !isCompany && landing.requiresName
  const needsPhone = !isCompany && landing.requiresPhone
  // Mirror-image for a company: its VAT is collected only where the row left it empty.
  const needsVatNumber = isCompany && landing.requiresVatNumber
  const [step, setStep] = useState<'welcome' | 'credentials'>('welcome')
  const [error, setError] = useState<string | null>(null)
  const [activate, { isLoading: isActivating }] = useActivateVeliClientMutation()

  const schema = useMemo(
    () =>
      isCompany
        ? buildCompanySchema(needsVatNumber)
        : buildIndividualSchema(needsName, needsPhone),
    [isCompany, needsName, needsPhone, needsVatNumber]
  )

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const wealthManager = landing.wealthManagerName
    ? `your wealth manager, ${landing.wealthManagerName},`
    : 'your wealth manager'

  if (step === 'welcome') {
    return (
      <VeliBrandedShell subtitle="Your account is moving from Veli to Penning.">
        <div className="card gap-4">
          <div className="text-center">
            <h1 className="font-semi text-xl mb-2">Welcome to Penning</h1>
            <p className="text-description">
              Veli is joining Penning, and {wealthManager} has set up your
              transfer. We'll walk you through every step — nothing changes about
              who looks after your money.
            </p>
          </div>

          <div className="rounded-md bg-bg-component p-4 text-center">
            {isCompany && landing.companyName && (
              <>
                <p className="text-description text-sm mb-1">Your company</p>
                <p className="font-semi mb-3">{landing.companyName}</p>
              </>
            )}
            <p className="text-description text-sm mb-1">Your account</p>
            <p className="font-semi">{landing.maskedEmail}</p>
          </div>

          <p className="text-description text-sm text-center">
            Next, you'll set a password to create your Penning account, then we'll
            guide you through Penning's standard onboarding.
          </p>

          <FormButton title="Continue" onClick={() => setStep('credentials')} />
        </div>
      </VeliBrandedShell>
    )
  }

  const onSubmit: SubmitHandler<FormData> = async ({
    confirmEmail,
    firstName,
    lastName,
    phone,
    vatNumber,
    password,
  }) => {
    setError(null)
    try {
      // The email is always fixed from the import row server-side (the confirmation
      // email is only an identity check, never the address), as is a company's legal
      // name. A company sends its phone. An individual sends a name/phone — and a
      // company its VAT — only where the row left a hole; the server uses those to fill
      // the gap and never to overwrite a value the row already holds.
      await activate({
        veliUserId,
        password,
        confirmedEmail: confirmEmail,
        ...(isCompany ? { phone } : {}),
        ...(needsName ? { firstName, lastName } : {}),
        ...(needsPhone ? { phone } : {}),
        ...(needsVatNumber ? { vatNumber } : {}),
      }).unwrap()
      // Veli origin is recorded server-side against the new user (the import row
      // is linked at activation), so the branded onboarding shell is driven by
      // the signed-in user — nothing to remember client-side here.
      //
      // Try to log the client straight in with the credentials they just set,
      // exactly as the signup screen does — the confirmed email matched the import
      // row server-side, so it's the real address, and the password is the one they
      // chose. On success, hard-navigate to /app so the Auth0 SDK re-initialises on
      // a fresh load and adopts the seeded session, dropping them straight into
      // onboarding. Falls back to the standard login redirect if auto-login isn't
      // possible (grant disabled, MFA, network) — the password works there too.
      const autoLoggedIn = await attemptAutoLoginAfterSignup(
        confirmEmail,
        password
      )
      if (autoLoggedIn) {
        window.location.assign('/app')
      } else {
        history.push('/login')
      }
    } catch (err) {
      // The API surfaces domain errors as `errors: string`, but a model-binding
      // failure surfaces `errors` as a validation dictionary/array. Only ever set
      // a string into state — anything else would render an object as a React
      // child and blank the page — falling back to the generic message.
      const errors = (err as { data?: { errors?: unknown } })?.data?.errors
      setError(
        typeof errors === 'string' && errors.length > 0
          ? errors
          : 'We could not complete your activation. Please try again, or contact support@penning.io.'
      )
    }
  }

  return (
    <VeliBrandedShell subtitle="Set a password to create your Penning account.">
      <div className="card gap-4">
        <div className="text-center">
          <h1 className="font-semi text-xl mb-1">Create your account</h1>
          <p className="text-description text-sm">
            {isCompany
              ? 'Your company details are fixed from your Veli record — just choose a password.'
              : needsName || needsPhone
                ? 'Your Veli record is missing a few details — add them below and choose a password.'
                : 'Your details are filled in from your Veli record — just choose a password.'}
          </p>
        </div>

        <div className="rounded-md bg-bg-component p-4 text-center">
          {isCompany && landing.companyName && (
            <>
              <p className="text-description text-sm mb-1">Company</p>
              <p className="font-semi mb-3">{landing.companyName}</p>
            </>
          )}
          {!isCompany && landing.maskedName && (
            <>
              <p className="text-description text-sm mb-1">Your name</p>
              <p className="font-semi mb-3">{landing.maskedName}</p>
            </>
          )}
          <p className="text-description text-sm mb-1">Your account email</p>
          <p className="font-semi">{landing.maskedEmail}</p>
          {!isCompany && landing.maskedPhone && (
            <>
              <p className="text-description text-sm mt-3 mb-1">Your phone</p>
              <p className="font-semi">{landing.maskedPhone}</p>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <FormInput
              label="Confirm your Veli email"
              inputMode="email"
              type="email"
              placeholder="name@example.com"
              registeredForm={register('confirmEmail')}
              error={errors?.confirmEmail}
            />
            <p className="text-description text-xs mt-1">
              For your security, type the full email your Veli account uses — it's
              shown above with most of it hidden. This isn't a new email and you can't
              change it here; we just need to confirm it's really you.
            </p>
          </div>
          {needsName && (
            <>
              <FormInput
                label="First name"
                inputMode="text"
                registeredForm={register('firstName')}
                error={errors?.firstName}
              />
              <FormInput
                label="Last name"
                inputMode="text"
                registeredForm={register('lastName')}
                error={errors?.lastName}
              />
            </>
          )}
          {(isCompany || needsPhone) && (
            <FormInput
              label="Phone"
              inputMode="tel"
              registeredForm={register('phone')}
              error={errors?.phone}
            />
          )}
          {needsVatNumber && (
            <div>
              <FormInput
                label="VAT number"
                inputMode="text"
                placeholder="SI12345678"
                registeredForm={register('vatNumber')}
                error={errors?.vatNumber}
              />
              <p className="text-description text-xs mt-1">
                Your company's VAT number, including the country prefix. We'll verify
                it along with your company documents in the next step.
              </p>
            </div>
          )}
          <FormInput
            label="Password"
            inputMode="text"
            isSecure
            showPassword
            registeredForm={register('password')}
            error={errors?.password}
          />
          <FormInput
            label="Confirm password"
            inputMode="text"
            isSecure
            showPassword
            registeredForm={register('confirmPassword')}
            error={errors?.confirmPassword}
          />

          <AnimateHeight isVisible={error !== null}>
            <MessageBox title={error ?? ''} type="warning" />
          </AnimateHeight>

          <FormButton
            title="Create my account"
            isLoading={isActivating}
            loadingText="Creating your account"
          />
        </form>
      </div>
    </VeliBrandedShell>
  )
}

export default VeliActivationScreen
